import React, { useState } from "react";
import { Link } from "react-router";
import { CaaSImageFilters } from "@app/types/OptimisedImage";
import { BlockTitle } from "@blocks/Blocks";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import Container from "@components/Container/Container";
import GAData from "@components/GAData/GAData";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import Header from "@components/Header/Header";
import LoadMore from "@components/LoadMore/LoadMore";
import MetaTags from "@components/MetaTags/MetaTags";
import OptimisedImage from "@components/OptimisedImage";
import EventsAwardsFooter from "@events-awards/components/EventsAwardsFooter";
import { ImageModal } from "@events-awards/components/EventsAwardsGalleryImageModal";
import EventsAwardsLogoMenu from "@events-awards/components/EventsAwardsLogoMenu";
import { AWARDS_EVENTS_PAGE_ROW_DATA } from "@events-awards/utils/constants";
import { getAllImages } from "@events-awards/utils/helpers";
import {
  AwardDataRow,
  AwardType,
  EventsAwardsGalleryContextData,
} from "@events-awards/utils/types";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "@util/helpers";
import { useDetectAdBlock } from "adblock-detect-react";

/**
 * Component to render gallery year page for event and awards
 */
const EventsAwardsGalleryYearPageContent = ({
  title,
  articles,
  awardType,
}: EventsAwardsGalleryContextData): React.ReactElement => {
  const adBlockDetected = useDetectAdBlock();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [visibleImages, setVisibleImages] = useState(30);

  const awardData: AwardDataRow | undefined = AWARDS_EVENTS_PAGE_ROW_DATA.find(
    (data) => data.awardType === awardType
  );
  const awardPath = awardData?.blockTitleProps?.link || "";
  const allImages = getAllImages(articles);
  const hasMoreImages = allImages?.length > visibleImages;
  const imageFilters: CaaSImageFilters[] = [
    { w: 269, h: 202, dpr: 1, f: "webp" },
  ];
  const isSIAAward = awardType === AwardType.SIA;

  const handleImageClick = (imageUrl: string): void => {
    setSelectedImage(imageUrl);
    setModalOpen(true);
  };

  const handleLoadMore = async () => {
    setVisibleImages((prev) => prev + 30);
  };

  return (
    <div
      className="min-h-screen w-full bg-gray-50"
      data-testid="events-gallery-year-page-component"
    >
      <AdSettings
        adNames={["lb1", "prestitial", "catfish", "abm"]}
        path={`${awardPath}/gallery`}
        adTargets={[{ key: "page", value: "listing" }]}
      />

      <GAData
        title={`${awardData?.blockTitleProps?.text} Gallery`}
        level2="events awards"
        adblocker={adBlockDetected ? 1 : 0}
      />

      <MetaTags
        title={title}
        description={`The Business Times ${awardData?.blockTitleProps?.text} - ${awardData?.description}`}
        ogType="website"
        slug={awardPath}
      />

      <Ad
        adType="lb1"
        className="flex min-h-[50px] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[250px]"
      />

      <Header />

      <Container>
        <Row rootClassName="justify-center">
          <Column
            rootClassName={cn("w-full lg:w-8/12 mb-8", {
              "lg:w-11/12": isSIAAward,
            })}
          >
            <EventsAwardsLogoMenu awardType={awardType} />

            <div className={cn("py-8", { "mx-auto max-w-4xl": !isSIAAward })}>
              {isSIAAward ? (
                <BlockTitle
                  text={articles?.[0].title}
                  rootClassName="text-4xl md:text-6xl lg:text-8xl font-medium text-left mb-4"
                />
              ) : null}

              {!isSIAAward ? (
                <div className="mb-6 flex items-center gap-3 border-b border-gray-200">
                  <Link
                    to={`${awardData?.blockTitleProps?.link}/gallery`}
                    className="inline-flex items-center text-2xl text-gray-900 no-underline hover:text-gray-700"
                  >
                    <div className="flex items-center px-4 py-3">
                      <FontAwesomeIcon
                        icon={faChevronLeft}
                        className="mr-3"
                        size="sm"
                      />

                      <span className="font-medium">{articles?.[0].title}</span>
                    </div>
                  </Link>
                </div>
              ) : null}

              <div
                className={cn("grid grid-cols-1 gap-6 md:grid-cols-3", {
                  "grid-cols-2 gap-4 md:grid-cols-2 lg:grid-cols-4": isSIAAward,
                })}
              >
                {allImages?.slice(0, visibleImages).map((image) => {
                  const imageUrl =
                    image.relation?.fields?.["landscape-caas"]?.url;

                  if (typeof imageUrl === "undefined") return <></>;

                  return (
                    <div
                      key={image.id}
                      className="aspect-video cursor-pointer overflow-hidden bg-white shadow-sm"
                      onClick={() => {
                        handleImageClick(imageUrl);
                      }}
                    >
                      <OptimisedImage
                        src={imageUrl}
                        loading="lazy"
                        alt={
                          image.fields?.find((f) => f.name === "caption")
                            ?.value || ""
                        }
                        filters={imageFilters}
                      />
                    </div>
                  );
                })}
              </div>

              {hasMoreImages && (
                <div className="mt-8">
                  <LoadMore
                    onLoadMore={handleLoadMore}
                    hasMore={hasMoreImages}
                  />
                </div>
              )}
            </div>
          </Column>
        </Row>
      </Container>

      <EventsAwardsFooter awardType={awardType} />

      <ImageModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
        image={selectedImage}
      />

      <Ad adType="catfish" />

      <Ad adType="abm" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default EventsAwardsGalleryYearPageContent;
